import type { TranslationKey } from "../api/translationKeys";
import { resolveTranslationTokens } from "./resolveTranslationTokens";

/**
 * HTML-safe counterpart to resolveTranslationTokens — for rich text content
 * (Tiptap-authored HTML), where a blind string.replace across the whole
 * markup would also match {{key}}-shaped text inside tag attributes (e.g. a
 * link's href), not just visible text. Walks only genuine text nodes via the
 * browser's built-in DOMParser/TreeWalker and resolves each one individually
 * with the existing resolveTranslationTokens, leaving all markup/attributes
 * untouched.
 */
export function resolveHtmlTranslationTokens(
  html: string,
  translationsByKey: Record<string, TranslationKey["translations"]>,
  lang: string,
  fallbackLang: string
): string {
  if (typeof html !== "string" || html.length === 0) return html ?? "";

  const doc = new DOMParser().parseFromString(html, "text/html");
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  let node: Node | null;
  while ((node = walker.nextNode())) {
    if (node.nodeValue) {
      node.nodeValue = resolveTranslationTokens(
        node.nodeValue,
        translationsByKey,
        lang,
        fallbackLang
      ).resolved;
    }
  }
  return doc.body.innerHTML;
}
